import FeaturedDestinations from "@/components/home/FeaturedDestinations";
import WhatsAppButton from "@/components/home/WhatsAppButton";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Plane, Building2, Shield, Car, HeadphonesIcon, Briefcase, DollarSign, Scale, Heart, Star } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

const servicos = [
  {
    icone: <Plane className="h-8 w-8" />,
    titulo: "Passagens Aéreas",
    descricao: "Acesso exclusivo às melhores tarifas de centenas de companhias aéreas em todo o mundo",
  },
  {
    icone: <Building2 className="h-8 w-8" />,
    titulo: "Hospedagens",
    descricao: "Variedade de hotéis com preços competitivos e tarifas exclusivas",
  },
  {
    icone: <Shield className="h-8 w-8" />,
    titulo: "Seguro Viagem",
    descricao: "Parcerias com seguradoras de renome para oferecer a melhor proteção em suas viagens",
  },
  {
    icone: <Car className="h-8 w-8" />,
    titulo: "Aluguel de Carro",
    descricao: "Opções de aluguel com tarifas exclusivas e ampla variedade de veículos",
  },
  {
    icone: <HeadphonesIcon className="h-8 w-8" />,
    titulo: "Assistência de Viagem",
    descricao: "Suporte completo antes, durante e após a viagem, com diretrizes jurídicas",
  },
  {
    icone: <Briefcase className="h-8 w-8" />,
    titulo: "Viagem Corporativa",
    descricao: "Gestão e planejamento de viagens para empresas com soluções que economizam tempo",
  },
];

const diferenciais = [
  {
    icone: <DollarSign className="h-8 w-8" />,
    titulo: "Tarifas Exclusivas",
    descricao: "Oferecemos preços competitivos, garantindo as melhores condições para sua viagem",
  },
  {
    icone: <HeadphonesIcon className="h-8 w-8" />,
    titulo: "Acompanhamento Completo",
    descricao: "Suporte personalizado antes, durante e após sua viagem, para uma experiência tranquila",
  },
  {
    icone: <Scale className="h-8 w-8" />,
    titulo: "Assessoria Jurídica",
    descricao: "Orientação especializada para garantir segurança e tranquilidade em qualquer situação",
  },
  {
    icone: <Heart className="h-8 w-8" />,
    titulo: "Atendimento Humanizado",
    descricao: "Priorizamos um atendimento próximo e personalizado, focado nas suas necessidades",
  },
];

const depoimentos = [
  {
    nome: "Mayani Cristina",
    viagem: "Buenos Aires, Argentina",
    conteudo: "Gostaria de agradecer por ter feito parte do meu 2023, pois foram vocês que fizeram o sonho se tornar realidade em me ajudar com passagem para Argentina e acima de tudo, com preço excelente. Obrigada pelo atendimento e atenção excepcional do começo ao fim, sempre me ajudando e sempre disponível para sanar qualquer dúvida.",
    avaliacao: 5,
    foto: "/testimonials/mayane.jpg",
  },
  {
    nome: "Lucas Machado",
    viagem: "Viagem a Trabalho",
    conteudo: "Em 2023, participei de diversas viagens, pois com a A Bordo Viagens. Desde a compra das passagens até a eficiência no processo de check-in, a equipe demonstrou comprometimento em garantir uma experiência tranquila e confortável. Todas as passagens foram adquiridas a preços mais acessíveis do que inicialmente previsto.",
    avaliacao: 5,
    foto: "/testimonials/lucas.jpg",
  },
  {
    nome: "Guilherme Braga",
    viagem: "Bangkok, Tailândia",
    conteudo: "A @a.bordoviagens nos ajudou com parte das passagens aéreas. Mesmo depois de um tempo entendendo e nos virando sozinhos, eles sempre achavam algo mais em conta, mais confortável ou que valesse mais a pena. Nos ajudaram com muito cuidado e se preocuparam muito com a nossa viagem.",
    avaliacao: 5,
    foto: "/testimonials/guilherme.jpg",
  },
  {
    nome: "Alexandre Arocha",
    viagem: "Viagem a Trabalho",
    conteudo: "Graças ao @a.bordoviagens, eu decidi vir... conseguiram baixar pela METADE do preço... e voltarei para as demais etapas com eles!",
    avaliacao: 5,
    foto: "/testimonials/alexandre.png"
  },
  {
    nome: "Luis Fernando Souza",
    viagem: "Munique, Alemanha",
    conteudo: "Viagem com planejamento excelente e acompanhamento de perto!",
    avaliacao: 5,
    foto: "/testimonials/fernando.jpg"
  },
  {
    nome: "Elias Pedro",
    viagem: "Viagem a Trabalho",
    conteudo: "Excelente agência de viagens, sempre disposta a ajudar e esclarecer sobre questões de valores, itinerários e política de troca e remarcação de passagens.",
    avaliacao: 5,
    foto: "/testimonials/elias.png"
  }
];

const Home = () => {
  const servicesRef = useRef(null);
  const differentialsRef = useRef(null);
  const isServicesInView = useInView(servicesRef, { once: true, margin: "-100px" });
  const isDifferentialsInView = useInView(differentialsRef, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      x: -100,
      opacity: 0 
    },
    visible: { 
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 100
      }
    }
  };

  const differentialsCardVariants = {
    hidden: { 
      x: 100,
      opacity: 0 
    },
    visible: { 
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 100
      }
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section with Background */}
      <div 
        className="relative bg-cover bg-center bg-no-repeat w-full min-h-[calc(100vh-64px)]"
      >
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url('/beach-background.png')`
          }}
        >
          {/* Background gradient layers */}
          <div className="absolute inset-0">
            {/* Top dark overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/30 to-transparent" />
            {/* Bottom transition gradient */}
            <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-gradient-to-b from-transparent via-[#fcfcfb]/5 to-[#fcfcfb]" />
          </div>
        </div>
        
        {/* Content - positioned above gradients */}
        <div className="relative h-full z-10">
          <div className="container mx-auto px-4 py-6 md:py-8 h-full flex flex-col max-w-[2000px] 2xl:px-8">
            <h1 
              className="font-['MontserratAlt1'] font-light text-4xl text-white text-center mb-4 pt-8"
            >
              Experiências Proporcionadas
            </h1>
            <div className="flex-1 flex flex-col justify-center max-w-[1800px] mx-auto w-full pb-12 md:pb-16">
              <FeaturedDestinations />
            </div>
          </div>
        </div>
      </div>

      {/* Rest of the content */}
      <div className="bg-[#fcfcfb] w-full relative">
        <section ref={servicesRef} className="container mx-auto px-4 py-12 md:py-16">
          <motion.div
            initial="hidden"
            animate={isServicesInView ? "visible" : "hidden"}
            variants={containerVariants}
          >
            <h2 className="font-['MontserratAlt1'] font-light text-4xl text-[#132034] mb-6 sm:mb-8 lg:mb-12 text-center">
              Nossos Serviços
            </h2>
            <p className="text-center text-[#666] text-sm md:text-base mb-8 md:mb-12 max-w-2xl mx-auto">
              Soluções completas para todas as suas necessidades de viagem
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {servicos.map((servico, index) => {
                const isEven = index % 2 === 0;
                const isThird = index % 3 === 2;
                return (
                  <motion.div
                    key={index}
                    variants={cardVariants}
                  >
                    <Card 
                      className={cn(
                        "group relative overflow-hidden border-none shadow-lg hover:shadow-xl transition-all duration-300",
                        isEven ? "bg-white" : isThird ? "bg-[#b3855e]" : "bg-[#b3855e]" 
                      )}
                    >
                      <CardContent className="p-8">
                        <div className="mb-6 flex justify-center">
                          <div className={cn(
                            "p-4 rounded-full group-hover:scale-110 transition-transform duration-300",
                            isEven ? "bg-[#132034]/5 text-[#b3855e]" : isThird ? "bg-[#fcfcfb]/10 text-[#fcfcfb]" : "bg-[#fcfcfb]/10 text-[#fcfcfb]"
                          )}>
                            {servico.icone}
                          </div>
                        </div>
                        <h3 className={cn(
                          "text-xl font-['Grostick'] text-center mb-3",
                          isEven ? "text-[#132034]" : "text-[#fcfcfb]"
                        )}>
                          {servico.titulo}
                        </h3>
                        <p className={cn(
                          "text-center",
                          isEven ? "text-[#666]" : "text-[#fcfcfb]/80"
                        )}>
                          {servico.descricao}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </section>

        <section className="bg-[#132034] py-16" ref={differentialsRef}>
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              animate={isDifferentialsInView ? "visible" : "hidden"}
              variants={containerVariants}
            >
              <h2 className="font-['MontserratAlt1'] font-light text-4xl text-[#fcfcfb] text-center mb-4">
                Nossos Diferenciais
              </h2>
              <p className="text-center text-[#fcfcfb]/80 mb-12 max-w-2xl mx-auto">
                O que nos destaca no mercado de viagens
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {diferenciais.map((diferencial, index) => (
                  <motion.div
                    key={index}
                    variants={differentialsCardVariants}
                  >
                    <Card 
                      className="group bg-[#fcfcfb]/5 border-none text-[#fcfcfb] hover:bg-[#fcfcfb]/10 transition-all duration-300"
                    >
                      <CardContent className="p-8">
                        <div className="mb-6 flex justify-center">
                          <div className="p-4 rounded-full bg-[#b3855e]/20 text-[#b3855e] group-hover:scale-110 transition-transform duration-300">
                            {diferencial.icone}
                          </div>
                        </div>
                        <h3 className="text-xl font-['Grostick'] text-center mb-3">
                          {diferencial.titulo}
                        </h3>
                        <p className="text-[#fcfcfb]/80 text-center">{diferencial.descricao}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="container mx-auto px-4">
          <h2 className="font-['MontserratAlt1'] font-light text-4xl text-[#132034] text-center mb-4 pt-8">
            Nossos Tripulantes Dizem
          </h2>
          <p className="text-center text-[#666] mb-12 max-w-2xl mx-auto">
            Experiências autênticas dos nossos viajantes que confiaram em nossos serviços
          </p>

          <Carousel
            opts={{
              align: "start",
              loop: true,
              skipSnaps: false,
              dragFree: true,
            }}
            className="w-full"
          >
            <CarouselContent>
              {depoimentos.map((depoimento, index) => (
                <CarouselItem key={index} className="sm:basis-1/2 lg:basis-1/3">
                  <Card className="group bg-white border-none shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-8">
                      <div className="flex flex-col items-center">
                        <div className="relative mb-6">
                          <Avatar className="w-24 h-24 border-4 border-[#b3855e]/20">
                            <AvatarImage 
                              src={depoimento.foto} 
                              alt={depoimento.nome}
                              className="object-cover"
                            />
                            <AvatarFallback className="bg-[#132034] text-white">
                              {depoimento.nome.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex items-center bg-[#b3855e] px-4 py-1 rounded-full">
                            {[...Array(depoimento.avaliacao)].map((_, i) => (
                              <Star key={i} className="h-4 w-4 fill-white text-white" />
                            ))}
                          </div>
                        </div>
                        <div className="text-center space-y-4">
                          <div>
                            <h3 className="font-['Grostick'] text-xl text-[#132034] font-semibold">
                              {depoimento.nome}
                            </h3>
                            <p className="text-sm text-[#b3855e] font-medium mt-1">
                              {depoimento.viagem}
                            </p>
                          </div>
                          <p className="text-[#666] text-sm leading-relaxed italic">
                            "{depoimento.conteudo}"
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </section>
      </div>
      <WhatsAppButton />
    </div>
  );
}

export default Home;